import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDeal } from "../lib/store";
import { shareApi, firmApi, docsApi } from "../lib/api";
import { pdfFromContainer } from "../lib/pdf";
import { toast } from "sonner";
import { Card, useCompose, ComposeEmailModal, ScenarioBar } from "../components/shared";
import { DeckPages, PRINTABLE_SECTIONS, PRINTABLE, INVESTOR_DEFAULT, LENDER_DEFAULT } from "../components/room/DeckPages";
import { dealKeyFor, dealKeyAlts } from "../components/ShareRoomModal";
import { roomModel } from "../lib/roomModel";
import * as m from "../lib/model";
import { PrintDeck } from "../components/room/PrintDeck";

import { PackageConfig } from "../components/PackageConfig";

const MODES = [
  { id: "investor", label: "Investor package" },
  { id: "lender", label: "Lender package" },
];
const fileSafe = (s) => (s || "deal").replace(/[^a-zA-Z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export default function TeaserGenerator() {
  const { M, V, scenario, setScenario, dealId, industry: dealIndustry, market } = useDeal();
  const [mode, setMode] = useState("investor");
  // The package is built off the chosen case, so a downside teaser prints downside numbers everywhere.

  const [codeName, setCodeName] = useState("Project Summit");
  const [location, setLocation] = useState("DFW Metroplex");
  const [industry, setIndustry] = useState(dealIndustry);
  useEffect(() => { setIndustry(dealIndustry); }, [dealIndustry]);
  const [printing, setPrinting] = useState(false);
  const [saving, setSaving] = useState(false);
  // Package state
  const [share, setShare] = useState(null);
  const [firm, setFirm] = useState(null);
  const [secsFor, setSecsFor] = useState({
    investor: Object.fromEntries(PRINTABLE.map((id) => [id, INVESTOR_DEFAULT.includes(id)])),
    lender: Object.fromEntries(PRINTABLE.map((id) => [id, LENDER_DEFAULT.includes(id)])),
  });
  const [switching, setSwitching] = useState(false);
  const pkgSecs = secsFor[mode];
  const setPkgSecs = (next) => setSecsFor((s) => ({ ...s, [mode]: next }));
  const [hideName, setHideName] = useState(true);
  const [hideAddr, setHideAddr] = useState(true);
  const [anonPipe, setAnonPipe] = useState(true);
  const [prepFor, setPrepFor] = useState("");
  const [prepEmail, setPrepEmail] = useState("");
  const [coverPhoto, setCoverPhoto] = useState("");
  const docRef = useRef(null);
  const compose = useCompose();

  useEffect(() => {
    const key = dealKeyFor(dealId, M);
    // The package mirrors the published room: same code name, same region, same blind promise.
    shareApi.byDeal(key, dealKeyAlts(dealId, M)).then((s) => {
      setShare(s);
      if (!s) return;
      if (s.name) setCodeName(s.name);
      if (s.region) setLocation(s.region);
      setHideName(!!s.blind);
      setHideAddr(!!s.blind);
    }).catch(() => setShare(null));
    firmApi.get().then(setFirm).catch(() => {});
  }, [dealId, M.dealKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Each audience keeps its own section picks, and the page holds its scroll position while it swaps.
  const pickAudience = (id) => {
    if (id === mode) return;
    const y = window.scrollY;
    setSwitching(true);
    setMode(id);
    requestAnimationFrame(() => window.scrollTo({ top: y }));
    setTimeout(() => setSwitching(false), 420);
  };

  const displayName = hideName ? codeName : (M.companyName || M.dealName || codeName);
  const locLabel = hideAddr ? location : (M.address || location);

  // Package: narrative from the published deal room, numbers from the live model.
  const MS = V;                      // app-wide case, shared with every other page

  const pkg = useMemo(() => {
    const base = share || {};
    const data = {
      ...base,
      sections: PRINTABLE.filter((id) => pkgSecs[id]),
      blind: anonPipe,
      snapshot: { ...(base.snapshot || {}), M: MS, firm: (base.snapshot && base.snapshot.firm) || firm, market: (base.snapshot && base.snapshot.market) || market },
      thesis: base.thesis || {},
      pipeline: base.pipeline || [],
      team: base.team || [],
      timeline: base.timeline || [],
      classes: base.classes || [],
    };
    const V = roomModel(data, MS).M;
    const MB = { ...V, companyName: displayName, dealName: displayName, address: locLabel, industry };
    const wf = m.lpWaterfall(MB);
    return { data, MB, ask: base.equityAsk > 0 ? base.equityAsk : m.lpAskOf(MB), platform: (base.snapshot && base.snapshot.platform) || null, market: data.snapshot.market };
  }, [share, firm, market, pkgSecs, anonPipe, MS, displayName, locLabel, industry]);

  const ex = m.exitReturns(MS);
  const dscr = m.dscrYr1(MS);
  const pageCount = 1 + PRINTABLE.filter((id) => pkgSecs[id]).length;
  const fileName = `XYZ-Ventures-${mode === "lender" ? "Credit-Package" : "Investor-Package"}-${fileSafe(displayName)}${scenario === "base" ? "" : `-${fileSafe(scenario)}`}.pdf`;


  const emailIt = () => {
    const label = mode === "lender" ? "Credit Package" : "Investor Package";
    compose.start({
      title: `Email ${label}`,
      recipients: prepEmail ? [{ name: prepFor, email: prepEmail }] : [],
      subject: `${mode === "lender" ? "Financing opportunity" : "Investment opportunity"} — ${displayName}`,
      body: `Hi ${prepFor ? prepFor.split(" ")[0] : "there"},\n\nAttached is our ${label.toLowerCase()} for **${displayName}** — a ${String(industry).toLowerCase()} business in the ${locLabel}.\n\n${mode === "lender"
        ? `We are seeking **${m.fmt$(m.loanAmt(MS))}** of senior debt at a modeled Year-1 DSCR of **${m.fmtX(dscr)}**, behind **${m.fmt$(ex.equityInvested)}** of sponsor equity.`
        : `We are raising **${m.fmt$(pkg.ask)}** of LP equity behind a ${M.prefReturn}% preferred return.`}\n\nHappy to walk you through the model on a call.\n\nBest regards,\n${[M.analystName, M.analystEmail, M.analystPhone].filter(Boolean).join(" · ") || "XYZ Ventures"}`,
      kind: mode === "lender" ? "lenders" : "investors",
      dealName: displayName,
      M: MS,
      attachLabel: label,
      teaserFilename: fileName,
      pdfContainer: () => docRef.current,
      vars: [["Deal name", displayName], ["Industry", industry], ["Location", locLabel], ["Case", scenario.toUpperCase()]],
    });
  };

  const saveToDocs = async () => {
    if (!docRef.current) return;
    setSaving(true);
    try {
      const { pdf } = await pdfFromContainer(docRef.current);
      const content = pdf.output("datauristring").split(",")[1];
      await docsApi.saveBase64({
        dealKey: dealKeyFor(dealId, M), dealName: M.dealName || displayName,
        category: mode === "lender" ? "Lender Teaser" : "Investor Teaser",
        label: `${mode === "lender" ? "Credit" : "Investor"} package — ${displayName}${scenario === "base" ? "" : ` (${scenario} case)`}`,
        filename: fileName, content,
      });
      toast.success("Saved to the Docs Room");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save the package"); }
    finally { setSaving(false); }
  };

  if (printing) {
    return <PrintDeck data={pkg.data} M={pkg.MB} title={displayName} platform={pkg.platform} market={pkg.market}
      ask={pkg.ask} investor={prepFor} audience={mode} coverPhoto={coverPhoto} onDone={() => setPrinting(false)} />;
  }

  return (
    <div className="page" data-testid="page-teaser">
      <div className="page-head">
        <div className="page-title">Teaser Generator</div>
        <div className="page-sub">Room-style investor and lender packages — auto-built from the live deal model</div>
      </div>

      <Card title="What are you sending?" testid="teaser-mode-card">
        <div className="tone-bar mode-bar" data-testid="teaser-mode">
          {MODES.map((x) => (
            <button key={x.id} className={mode === x.id ? "active" : ""} onClick={() => pickAudience(x.id)} data-testid={`teaser-mode-${x.id}`}>
              {x.label}{switching && mode === x.id ? <i className="mode-spin" /> : null}
            </button>
          ))}
        </div>
        <div className="ir-fine" style={{ marginTop: 8 }}>
          {mode === "lender"
            ? "The pages your lenders need for credit review — overview, financials, structure, coverage and risks — straight off the deal room."
            : "Everything an investor sees in the deal room, on paper: cover, thesis, market, financials, returns, structure, pipeline, team and risks."}
          {share ? ` Narrative, code name${share.region ? " and region" : ""} are pulled from your published deal room, so this matches what investors see.` : " No deal room published yet — the narrative falls back to the live model."}
        </div>
      </Card>

      <PackageConfig
          audience={mode} secs={pkgSecs} setSecs={setPkgSecs} sections={PRINTABLE_SECTIONS}
          hideName={hideName} setHideName={setHideName} hideAddr={hideAddr} setHideAddr={setHideAddr}
          anonPipe={anonPipe} setAnonPipe={setAnonPipe} codeName={codeName} setCodeName={setCodeName}
          location={location} setLocation={setLocation} address={M.address}
          prepFor={prepFor} setPrepFor={setPrepFor} prepEmail={prepEmail} setPrepEmail={setPrepEmail}
          coverPhoto={coverPhoto} setCoverPhoto={setCoverPhoto}
        />

      <Card
        title={`${mode === "lender" ? "Credit" : "Investor"} Package Preview · ${pageCount} pages · ${scenario.toUpperCase()} case`}
        style={{ marginTop: 12 }}
        right={(
          <span style={{ display: "flex", gap: 6, alignItems: "center" }} className="no-print">
            <ScenarioBar value={scenario} onChange={setScenario} />
            <button className="btn btn-primary" onClick={() => setPrinting(true)} data-testid="teaser-pdf">Print / Save as PDF</button>
            <button className="btn" onClick={saveToDocs} disabled={saving} data-testid="teaser-save-docs">{saving ? "Saving…" : "Save to Docs Room"}</button>
            <button className="btn" onClick={emailIt} data-testid="teaser-email">Email to {mode === "lender" ? "Lender" : "Investor"}</button>
          </span>
        )}
      >
        <div className={`pkg-preview${switching ? " swapping" : ""}`} ref={docRef} data-testid="package-doc">
          <DeckPages data={pkg.data} M={pkg.MB} title={displayName} platform={pkg.platform} market={pkg.market}
            ask={pkg.ask} investor={prepFor} audience={mode} coverPhoto={coverPhoto} />
        </div>
      </Card>
      <ComposeEmailModal open={compose.open} onClose={() => compose.setOpen(false)} config={compose.config} />
    </div>
  );
}
